import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Visibility as ViewIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import {
  OrderWithClient,
  getClientInfo,
  ClientInfo,
} from "../../utils/adminOrderUtils";

interface ClientsTabProps {
  orders: OrderWithClient[];
  onRefresh: () => void;
}

const ClientsTab: React.FC<ClientsTabProps> = ({ orders, onRefresh }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [clients, setClients] = useState<ClientInfo[]>([]);
  const [selectedClient, setSelectedClient] = useState<ClientInfo | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadClientData();
  }, [orders]);

  const loadClientData = async () => {
    try {
      setLoading(true);
      const clientData = await getClientInfo();
      setClients(clientData);
    } catch (error) {
      console.error("Error loading client data:", error);
      enqueueSnackbar("Error loading client data", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleViewClient = (client: ClientInfo) => {
    setSelectedClient(client);
  };

  const handleEmailClient = (email: string) => {
    window.open(`mailto:${email}`, "_blank");
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const getStatusChip = (status: string) => {
    const statusColors: Record<
      string,
      | "default"
      | "primary"
      | "secondary"
      | "error"
      | "info"
      | "success"
      | "warning"
    > = {
      pending: "warning",
      "awaiting-info": "info",
      "in-progress": "primary",
      completed: "success",
      cancelled: "error",
    };

    return (
      <Chip
        label={status}
        color={statusColors[status] || "default"}
        size="small"
      />
    );
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">Client Management</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={onRefresh}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : clients.length === 0 ? (
        <Alert severity="info">No clients found.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Client</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Total Orders</TableCell>
                <TableCell>Total Spent</TableCell>
                <TableCell>Last Order</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.map((client) => (
                <TableRow key={client.email}>
                  <TableCell>
                    <Typography variant="body2">
                      {client.name || "No name provided"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{client.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {client.totalOrders}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatCurrency(client.totalSpent)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(client.lastOrderDate)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewClient(client)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Send Email">
                        <IconButton
                          size="small"
                          onClick={() => handleEmailClient(client.email)}
                        >
                          <EmailIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Client Details Dialog */}
      <Dialog
        open={!!selectedClient}
        onClose={() => setSelectedClient(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Client Details</DialogTitle>
        <DialogContent>
          {selectedClient && (
            <Box sx={{ pt: 2 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {selectedClient.name || "No name provided"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedClient.email}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 2,
                  mb: 3,
                }}
              >
                <Box>
                  <Typography variant="subtitle2">Total Orders</Typography>
                  <Typography variant="h6">
                    {selectedClient.totalOrders}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Total Spent</Typography>
                  <Typography variant="h6">
                    {formatCurrency(selectedClient.totalSpent)}
                  </Typography>
                </Box>
              </Box>

              <Typography variant="h6" gutterBottom>
                Order History
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Order ID</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedClient.orders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {order.id.substring(0, 8)}...
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {order.productType || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>{getStatusChip(order.status)}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {order.totalAmount
                              ? formatCurrency(order.totalAmount)
                              : "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(order.createdAt)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedClient(null)}>Close</Button>
          {selectedClient && (
            <Button
              variant="contained"
              startIcon={<EmailIcon />}
              onClick={() => handleEmailClient(selectedClient.email)}
            >
              Send Email
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientsTab;
